import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'next-i18next'
import tw from 'tailwind-styled-components'
import capitalize from 'lodash/capitalize'
import { Button } from '../../Button'
import { formatArea, formatValueMoney, formatVolume } from '../../../utils'
import { LayerPopup, Props as LayerPopupProps } from './LayerPopup'
import { usePopupTaxonomicPlotQuery, useModalTaxonomicPlotQuery } from '@foros-fe/graphql'
import { MapModal } from '../Controls/MapModal'

type Props = Pick<LayerPopupProps, 'position'> & {
  id: string
  cadastralPlotId?: string
}

const Title = tw.p`text-2xl text-center mb-6 font-medium`
const Line = tw.div`flex justify-between gap-4`

export const TaxonomicPlotPopup: FC<Props> = ({ position, id, cadastralPlotId }) => {
  const { t } = useTranslation(['forest', 'auctions', 'common'])
  const [modalOpen, setModalOpen] = useState(false)
  const toggleModal = useCallback(() => setModalOpen((flag) => !flag), [])
  const { data: popupData } = usePopupTaxonomicPlotQuery({ variables: { id, cadastralPlotId } })
  const { data: modalData } = useModalTaxonomicPlotQuery({
    variables: { id, cadastralPlotId },
    skip: !modalOpen,
  })

  const popupTaxonomicPlot = popupData?.taxonomicPlot
  const modalTaxonomicPlot = modalData?.taxonomicPlot

  return (
    <>
      <LayerPopup position={position} layer='taxonomicPlots' title={t('common:map.taxonomicPlot')}>
        {popupTaxonomicPlot ? (
          <>
            <Line>
              <LayerPopup.Item
                label={t('taxonomicPlot.identifier')}
                value={popupTaxonomicPlot.mapIdentifier}
              />
              <LayerPopup.Item label={t('area')} value={formatArea(popupTaxonomicPlot.area)} />
            </Line>
            <Line>
              <LayerPopup.Item
                label={t('taxonomicPlot.composition')}
                value={popupTaxonomicPlot.speciesComposition}
              />
              <LayerPopup.Item
                label={t('taxonomicPlot.age')}
                value={
                  popupTaxonomicPlot.age
                    ? t('common:years', { count: popupTaxonomicPlot.age })
                    : null
                }
              />
            </Line>
            <Button
              title={t('auctions:showMore')}
              value='taxonomic-plot-popup-more'
              onClick={toggleModal}
            />
          </>
        ) : null}
      </LayerPopup>
      <MapModal open={modalOpen} onClose={toggleModal}>
        <Title>{t('common:map.taxonomicPlot')}</Title>
        {modalTaxonomicPlot ? (
          <div className='grid grid-cols-3 gap-4'>
            <LayerPopup.Item
              label={t('taxonomicPlot.identifier')}
              value={modalTaxonomicPlot.mapIdentifier}
            />
            <LayerPopup.Item
              label={t('taxonomicPlot.composition')}
              value={modalTaxonomicPlot.speciesComposition}
            />
            <LayerPopup.Item label={t('area')} value={formatArea(modalTaxonomicPlot.area)} />
            <LayerPopup.Item
              label={`${capitalize(t('auctions:volume'))}, ${t('common:cubicMeters')}`}
              value={formatVolume(modalTaxonomicPlot.volume, '')}
            />
            <LayerPopup.Item
              label={t('marketValue')}
              value={formatValueMoney(modalTaxonomicPlot.marketValue)}
            />
            <LayerPopup.Item
              label={t('totalValue')}
              value={formatValueMoney(modalTaxonomicPlot.totalValue)}
            />
            <LayerPopup.Item
              label={t('taxonomicPlot.crownDensity')}
              value={modalTaxonomicPlot.crownDensity}
            />
            <LayerPopup.Item
              label={`${capitalize(t('auctions:volume'))}, ${t('common:cubicMeters')}/${t(
                'common:hectare'
              )}`}
              value={formatVolume(modalTaxonomicPlot.volumePerHa, '')}
            />
            <LayerPopup.Item
              label={t('taxonomicPlot.growingPlace')}
              value={modalTaxonomicPlot.growingPlace}
            />
            <LayerPopup.Item
              label={capitalize(t('forestGroups'))}
              value={
                modalTaxonomicPlot.forestGroups?.length
                  ? modalTaxonomicPlot.forestGroups.join(', ')
                  : t('notSpecified')
              }
            />
            <LayerPopup.Item
              label={t('taxonomicPlot.age')}
              value={
                modalTaxonomicPlot.age ? t('common:years', { count: modalTaxonomicPlot.age }) : null
              }
            />
            {modalTaxonomicPlot.forestHabitatPresence ? (
              <LayerPopup.Item label={t('common:map.forestHabitat')} value={t('common:yes')} />
            ) : null}
          </div>
        ) : null}
      </MapModal>
    </>
  )
}
